import React, { useMemo } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';

const {
  FiExternalLink, FiFolder, FiBookmark, FiPlus,
} = FiIcons;

const COLUMNS = [
  { id: 'inbox', label: 'Inbox & New', color: '#38bdf8', border: 'border-sky-500/20' },
  { id: 'reading', label: 'Reading Queue', color: '#c084fc', border: 'border-purple-500/20' },
  { id: 'work', label: 'Active Projects', color: '#34d399', border: 'border-emerald-500/20' },
  { id: 'reference', label: 'Reference & Vault', color: '#fbbf24', border: 'border-amber-500/20' },
];

export default function KanbanBoard({
  data = {},
  onOpenBookmark,
  onItemClick,
  onContextMenu,
  themeColors: tc,
  isDark = true,
}) {
  const [swimlanePriority, setSwimlanePriority] = React.useState('all');

  const columnData = useMemo(() => {
    const buckets = {
      inbox: [],
      reading: [],
      work: [],
      reference: [],
    };

    Object.values(data).forEach((folder) => {
      (folder.items || []).forEach((item) => {
        if (item.type === 'link') {
          const t = (item.title || '').toLowerCase();
          const n = (item.note || '').toLowerCase();

          // Swimlane priority filter
          if (swimlanePriority === 'high' && !n.includes('#high') && !item.pinned) return;
          if (swimlanePriority === 'active' && !n.includes('#active') && !n.includes('#work')) return;
          if (swimlanePriority === 'archive' && !n.includes('#archive')) return;

          if (t.includes('read') || n.includes('#read') || n.includes('article') || t.includes('guide')) {
            buckets.reading.push({ ...item, folderTitle: folder.title });
          } else if (n.includes('#project') || n.includes('#work') || t.includes('dev') || t.includes('app')) {
            buckets.work.push({ ...item, folderTitle: folder.title });
          } else if (n.includes('#vault') || item.credentials?.trim() || t.includes('doc') || t.includes('api')) {
            buckets.reference.push({ ...item, folderTitle: folder.title });
          } else {
            buckets.inbox.push({ ...item, folderTitle: folder.title });
          }
        }
      });
    });

    return buckets;
  }, [data, swimlanePriority]);

  return (
    <div className="w-full flex flex-col gap-4 pb-12">
      {/* Swimlane Priority Selector */}
      <div className="flex items-center justify-between pb-2 border-b border-white/5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-[#777] uppercase tracking-wider">Kanban Swimlanes:</span>
          {[
            { id: 'all', label: 'All Columns' },
            { id: 'high', label: '🔥 High Priority' },
            { id: 'active', label: '⚡ Active Work' },
            { id: 'archive', label: '📁 Reference Archive' },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setSwimlanePriority(s.id);
                playSound('click');
              }}
              className={`px-2.5 py-1 rounded-lg text-xs font-mono transition-all ${
                swimlanePriority === s.id
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold'
                  : 'bg-[#181820] text-[#777] border border-[#242430] hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <span className="text-[11px] font-mono text-[#555]">Dynamic Task Grouping</span>
      </div>

      <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4">
      {COLUMNS.map((col) => {
        const items = columnData[col.id] || [];
        return (
          <div
            key={col.id}
            className={`flex flex-col rounded-2xl bg-[#131317]/80 border ${col.border} p-3.5 backdrop-blur-md min-h-[420px]`}
          >
            {/* Column Header */}
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: col.color }}
                />
                <h3 className="text-xs font-bold uppercase tracking-wider text-white">
                  {col.label}
                </h3>
              </div>
              <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-white/5 text-[#888]">
                {items.length}
              </span>
            </div>

            {/* Column Items */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => { playSound('click'); onItemClick(item); }}
                  onContextMenu={(e) => onContextMenu(e, item)}
                  className="group p-3 rounded-xl bg-[#181820] border border-[#262630] hover:border-white/20 transition-all cursor-pointer shadow-sm hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {item.logoUrl ? (
                        <img
                          src={item.logoUrl}
                          alt=""
                          className="w-5 h-5 rounded object-contain shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <SafeIcon icon={FiBookmark} className="w-4 h-4 shrink-0 text-cyan-400" />
                      )}
                      <span className="text-xs font-semibold text-white truncate group-hover:text-cyan-300 transition-colors">
                        {item.title}
                      </span>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        playSound('launch');
                        onOpenBookmark(item.url);
                      }}
                      className="p-1 rounded text-[#666] hover:text-white opacity-40 group-hover:opacity-100 transition-opacity"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5 text-[10px] text-[#777]">
                    <span className="truncate max-w-[120px]">{item.folderTitle}</span>
                    {item.credentials?.trim() && (
                      <span className="text-cyan-400 font-mono font-medium">🔒 Encrypted</span>
                    )}
                  </div>
                </div>
              ))}

              {items.length === 0 && (
                <div className="py-12 text-center text-xs text-[#555] italic">
                  No items in this column
                </div>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
